<script>
    // Licensed under Gnu Public Licence version 2
    // Copyright (c) 2020 (original work) Open Assessment Technologies SA ;
    /**
     * @param {string} label - label which will be shown on the left/right of checkbox(depends on rtl)
     * @param {boolean} [checked=false] - checked
     * @param {boolean} [disabled=false] - disabled
     * @fires {svelte event} change - fires when checked has been changed
     */

    import { createEventDispatcher } from 'svelte';
    import Icon from '../icon/Icon.svelte';
    import { getActualKey } from '@oat-sa-private/ui-core';

    export let label = '';
    export let ariaLabel = '';
    export let checked = false;
    export let disabled = false;
    export let indeterminate = false;

    let checkboxElement = null;

    const dispatch = createEventDispatcher();

    /**
     * Just proxy function from native input event to dispatch new value to custom event
     * @param {Event} event - native input event object
     */
    function handleInput(event) {
        dispatch('change', event.target.checked);
    }

    /**
     * Keypress method checks if KB key is ENTER
     * when the correct key is pressed, the click action method is invoked
     * @param {KeyboardEvent} e keyboard key event
     */
    function checkIfAllowedKey(e) {
        const key = getActualKey(e);
        if (key === 'enter') {
            dispatch('change', (checked = !checked));
        }
    }

    function updateIndeterminateState(newIndeterminateState) {
        checkboxElement.indeterminate = newIndeterminateState;
    }

    $: showIconContainer = checked || indeterminate;

    $: indeterminateState = !checked && indeterminate;

    $: if (checkboxElement) {
        updateIndeterminateState(indeterminateState);
    }
</script>

<style>
    label {
        --checkboxSize: 2rem;
        --checkboxBorderRadius: 0.4rem;
        --iconTopOffset: -0.25rem;

        cursor: pointer;
        display: flex;
        position: relative;

        & .selection-container {
            position: absolute;
            width: 100%;
            height: 100%;
            z-index: var(--layer-1);
        }

        & .check-icon-container {
            position: relative;
            z-index: var(--layer-1);
            & .icon {
                top: var(--iconTopOffset);
                position: absolute;
            }
        }

        & .checkbox {
            position: absolute;
            opacity: 0;

            &:disabled,
            &:disabled + .selection-container {
                cursor: not-allowed;
                & ~ .label {
                    color: var(--color-text-disabled);
                }
                & ~ .selection::before {
                    border-color: var(--color-text-disabled);
                }
            }
        }

        & .checkbox ~ .selection::before {
            content: '';
            display: inline-block;
            width: var(--checkboxSize);
            height: var(--checkboxSize);
            border: var(--border-medium) solid var(--color-border-default);
            border-radius: var(--checkboxBorderRadius);
            position: relative;
            top: var(--border-medium);
            background: var(--color-bg-default);
        }

        & .selection-container:hover ~ .selection::before,
        & .checkbox:active ~ .selection::before {
            border-color: var(--color-brand-hover);
        }

        & .label {
            padding-inline-start: var(--space-1x);
        }
    }

    :global(label .checkbox:focus-visible ~ .selection-container) {
        @add-mixin simple-outline;

        & ~ .selection::before {
            border-color: var(--color-brand-hover);
        }
    }
</style>

<!-- svelte-ignore a11y-click-events-have-key-events -->
<label on:click>
    {#if showIconContainer}
        <div class="check-icon-container">
            {#if checked}
            <div class="icon">
                <Icon name="checkbox-check-16" />
            </div>
            {:else if indeterminate}
            <div class="icon">
                <Icon name="checkbox-minus-16" />
            </div>
            {/if}
        </div>
    {/if}
    <input
        type="checkbox"
        class="checkbox"
        {disabled}
        aria-checked={checked}
        aria-label={ariaLabel}
        bind:checked
        bind:this={checkboxElement}
        on:input={handleInput}
        on:keydown={e => checkIfAllowedKey(e)} />
    <span class="selection-container" />
    <span class="selection" />
    {#if label}<span class="label">{label}</span>{/if}
</label>
